// DS: MO3.4.2
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import OriginalBrush from '@wix/da-ds/pkg/Icons/16x16/OriginalBrush';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import DeviationShopCard, {
  type DeviationShopCardProps,
} from '../DeviationShopCard';
import { formatDollars } from '../../utils/formatting';
import { normalizeOriginalsCompletedOrders } from '../../utils/originalsCompletedOrders';

import s from './OriginalsShopCard.scss';

export interface Props
  extends Pick<DeviationShopCardProps, 'allowedIndicators'> {
  deviation: PapiDeviation;
  width: number;
  withAuthorDetails?: boolean;
  withCommentCount?: boolean;
  withDescriptionBlock?: boolean;
}

const OriginalsShopCard: React.FC<Props> = ({
  width,
  deviation,
  withAuthorDetails,
  withCommentCount,
  allowedIndicators,
  withDescriptionBlock = false,
}) => {
  const { t } = useTranslation();
  const originals = deviation.originals || deviation.extended?.originals;
  const { total: completedOrdersCount } =
    normalizeOriginalsCompletedOrders(originals);

  const belowCoverImageRightSlot =
    originals &&
    (completedOrdersCount === 0 ? (
      <div className={s['orders-count']}>
        {t('shop.thumb.originals.makeFirstOrder')}
      </div>
    ) : (
      <div className={s['orders-count']}>
        {t('shop.thumb.originals.totalOrders', {
          count: completedOrdersCount,
        })}
      </div>
    ));

  const detailsRightSlot = originals && (
    <Flex direction="column" className={s['details']}>
      <div className={s['base-price']}>
        <div className={s['base-price-dollars']}>
          {formatDollars(originals.basePrice)}
        </div>
        {t('shop.thumb.originals.basePrice')}
      </div>
    </Flex>
  );

  return (
    <DeviationShopCard
      width={width}
      deviation={deviation}
      withStats={true}
      withCommentCount={true}
      withDescriptionBlock={withDescriptionBlock}
      withAuthorDetails={withAuthorDetails}
      allowedIndicators={allowedIndicators}
      belowCoverImageRightSlot={belowCoverImageRightSlot}
      detailsRightSlot={detailsRightSlot}
      footerSlot={(
        <BuyButton
          size="medium"
          variant="outline"
          rounded
          fillContainerWidth
          icon={OriginalBrush}
        >
          {t('shop.thumbs.originals.explore')}
        </BuyButton>
      )}
    />
  );
};
OriginalsShopCard.displayName = 'OriginalsShopCard';

export default OriginalsShopCard;
